import React, { useState, useCallback, useMemo, createContext, useContext } from 'react';

/**
 * This hook lets you create a notification queue that can be manipulated. It accepts a notification type that describes
 * the shape of every notification that is allowed to be emitted. This doesn't provide any UI for the notifications.
 * This is left up to the end user to implement.
 *
 * This means you'll need to create your own NotificationProvider to create the queue, render the notifications, and
 * provide the queue API to your components so they can add notifications.
 *
 *      interface Data {
 *        message: string;
 *      }
 *
 *      function MyNotificationProvider() {
 *        const queue = useQueue<Data>();
 *
 *        return (
 *          queue.list.map(item => {
 *            return <div>{item.message}</div>
 *          })
 *        )
 *      }
 */

function useQueue(initialValue) {
  var _useState = useState(initialValue),
      entries = _useState[0].entries,
      setQueue = _useState[1];
  /**
   * Add a new notification to the queue. If the ID already exists it will updated.
   * @param id Unique string identifier for notification.
   * @param data
   */


  var add = useCallback(function (id, data) {
    setQueue(function (queue) {
      return queue.add(id, data);
    });
  }, [setQueue]);
  /**
   * Remove a notification by ID
   * @param id Unique string identifier for a notification
   */

  var remove = useCallback(function (id) {
    setQueue(function (queue) {
      return queue.remove(id);
    });
  }, [setQueue]);
  /**
   * Remove all notifications from the page.
   */

  var removeAll = useCallback(function () {
    setQueue(function (queue) {
      return queue.removeAll();
    });
  }, [setQueue]);
  return useMemo(function () {
    return {
      add: add,
      remove: remove,
      removeAll: removeAll,
      entries: entries
    };
  }, [add, remove, removeAll, entries]);
}
/**
 * Create an immutable queue. Adding and removing items will return a new queue.
 */

function createImmutableQueue(entries) {
  if (entries === void 0) {
    entries = [];
  }

  return {
    add: function add(id, data) {
      var matchIndex = entries.findIndex(function (n) {
        return n.id === id;
      });
      var copy = entries.slice();

      if (matchIndex > -1) {
        copy.splice(matchIndex, 1, {
          id: id,
          data: data
        });
      } else {
        copy.push({
          id: id,
          data: data
        });
      }

      return createImmutableQueue(copy);
    },
    remove: function remove(id) {
      return createImmutableQueue(entries.filter(function (n) {
        return n.id !== id;
      }));
    },
    removeAll: function removeAll() {
      return createImmutableQueue();
    },
    entries: entries
  };
}
/**
 * Create a mutable queue. Adding and removing items will return the same queue.
 */

function createMockImmutableQueue(initialValue) {
  if (initialValue === void 0) {
    initialValue = [];
  }

  var entries = initialValue;

  var updateEntries = function updateEntries(queue) {
    entries.splice(0, entries.length);
    entries.push.apply(entries, queue.entries);
  };

  return {
    add: function add(id, data) {
      var queue = createImmutableQueue(entries).add(id, data);
      updateEntries(queue);
      return this;
    },
    remove: function remove(id) {
      var queue = createImmutableQueue(entries).remove(id);
      updateEntries(queue);
      return this;
    },
    removeAll: function removeAll() {
      var queue = createImmutableQueue(entries).removeAll();
      updateEntries(queue);
      return this;
    },
    entries: entries
  };
}
/**
 * Create a React context and hook for accessing it typed to your notification shape.
 *
 *      const { NotificationQueueProvider, useNotifications } = createNotificationContext<Notification>();
 *
 * This allows you to skip some of the ceremony needed to setup your own notification renderer.
 */

function createNotificationContext() {
  var NotificationQueueContext = createContext(null);
  /**
   * This hook allows you to add and remove notifications from within your components.
   */

  function useNotificationQueue() {
    var queue = useContext(NotificationQueueContext);

    if (!queue) {
      throw new Error('Missing <NotificationProvider>');
    }

    return queue;
  }
  /**
   * This component should wrap your app. It allows components to use the useNotifications hook.
   * @param props
   */


  function NotificationProvider(props) {
    var children = props.children,
        initialQueue = props.queue;
    var queue = useQueue(initialQueue ? initialQueue : createImmutableQueue());
    return React.createElement(NotificationQueueContext.Provider, {
      value: queue
    }, children);
  }
  /**
   * Create a fake notification queue that can be used during tests and stories. Unlike the normal queue this one isn't
   * immutable, so you'll be able to inspect the state of the queue between renders:
   *
   *      const queue = createMockNotificationQueue();
   *
   *      const { findByText } = render(
   *        <MockNotificationQueueProvider queue={queue}>
   *          <MyComponent />
   *        </MockNotificationQueueProvider>
   *      );
   *
   *      const el = await findByText('Submit');
   *
   *      act(() => {
   *        fireEvent.click(el);
   *      })
   *
   *      expect(queue.list.length).toBe(1);
   *      expect(queue.list[0]).toEqual({ id: 'submitting', data: { message: 'Submitting...' } });
   *
   * This will let you test that notifications are correctly firing without needing to look for elements in the DOM.
   */


  function createNotificationQueue() {
    return createImmutableQueue();
  }

  function createMockNotificationQueue() {
    return createMockImmutableQueue();
  }

  return {
    NotificationQueueContext: NotificationQueueContext,
    NotificationProvider: NotificationProvider,
    useNotificationQueue: useNotificationQueue,
    createNotificationQueue: createNotificationQueue,
    createMockNotificationQueue: createMockNotificationQueue
  };
}

export { createImmutableQueue, createMockImmutableQueue, createNotificationContext, useQueue };
//# sourceMappingURL=react-notification-provider.esm.js.map
